import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { traa } from 'three/addons/tsl/display/TRAANode.js';
import {
  builtinAOContext,
  emissive,
  mrt,
  normalView,
  output,
  packNormalToRGB,
  pass,
  sample,
  screenUV,
  unpackRGBToNormal,
  velocity,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { QualitySettings } from './quality.ts';

export type Backend = 'WebGPU' | 'WebGL2';

export async function createRenderer(
  container: HTMLElement,
  quality: QualitySettings,
  forceWebGL: boolean,
): Promise<{ renderer: THREE.WebGPURenderer; backend: Backend }> {
  const renderer = new THREE.WebGPURenderer({ antialias: false, forceWebGL });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.toneMapping = THREE.AgXToneMapping;
  renderer.toneMappingExposure = 0.95;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  container.appendChild(renderer.domElement);
  await renderer.init();
  const isWebGPU = (renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend === true;
  return { renderer, backend: isWebGPU ? 'WebGPU' : 'WebGL2' };
}

/**
 * Post stack (docs/06): Low = scene + bloom (MSAA). Medium/High = normal/velocity pre-pass,
 * GTAO feeding the ambient term, TRAA, bloom. Tone mapping and sRGB output happen at the end.
 * Bloom is selective: it reads the emissive MRT channel, so only glowing materials (magma,
 * embers, glyphs, fireflies) bloom. The physically based sky is far brighter than lit
 * surfaces and would otherwise wash out the whole frame.
 */
export function createPipeline(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  quality: QualitySettings,
): THREE.RenderPipeline {
  const pipeline = new THREE.RenderPipeline(renderer);
  const scenePass = pass(scene, camera, { samples: quality.msaa });
  scenePass.setMRT(mrt({ output, emissive }));
  const glow = () => bloom(scenePass.getTextureNode('emissive'), 0.9, 0.45, 0);

  if (!quality.ao || !quality.temporalAA) {
    const color = scenePass.getTextureNode('output');
    pipeline.outputNode = quality.bloom ? color.add(glow()) : color;
    return pipeline;
  }

  const prePass = pass(scene, camera);
  prePass.transparent = false;
  prePass.setMRT(mrt({ output: packNormalToRGB(normalView), velocity }));
  prePass.getTexture('output').type = THREE.UnsignedByteType;
  const prePassDepth = prePass.getTextureNode('depth');
  const prePassVelocity = prePass.getTextureNode('velocity');
  const prePassNormal = sample((uv) => unpackRGBToNormal(prePass.getTextureNode().sample(uv)));

  const aoPass = ao(prePassDepth, prePassNormal, camera);
  aoPass.resolutionScale = quality.tier === 'high' ? 0.75 : 0.5;
  aoPass.radius.value = 0.6;
  aoPass.scale.value = 1.1;
  scenePass.contextNode = builtinAOContext(aoPass.getTextureNode().sample(screenUV).r);

  const aa = traa(scenePass, prePassDepth, prePassVelocity, camera);
  aa.useSubpixelCorrection = false;

  pipeline.outputNode = quality.bloom ? aa.add(glow()) : aa;
  return pipeline;
}

/**
 * Nudges the pixel ratio every second to hold ~60 fps (docs/06 dynamic resolution).
 */
export function createResolutionController(renderer: THREE.WebGPURenderer, maxPixelRatio: number) {
  const min = 0.6;
  const max = Math.min(window.devicePixelRatio, maxPixelRatio);
  let ratio = max;
  let frames = 0;
  let elapsed = 0;
  return {
    get ratio() {
      return ratio;
    },
    tick(dt: number) {
      frames++;
      elapsed += dt;
      if (elapsed < 1) return;
      const avgMs = (elapsed / frames) * 1000;
      frames = 0;
      elapsed = 0;
      let next = ratio;
      if (avgMs > 19) next = Math.max(min, ratio - 0.1);
      else if (avgMs < 14) next = Math.min(max, ratio + 0.05);
      if (Math.abs(next - ratio) > 0.001) {
        ratio = next;
        renderer.setPixelRatio(ratio);
      }
    },
  };
}
